import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import SEO from "@/components/SEO";

/* Static, fully-rendered privacy notice. Indexed by search engines. */
export default function Privacy() {
  return (
    <div className="min-h-screen bg-white text-[#0A0A0A]" data-testid="privacy-page">
      <SEO
        title="Privacy Notice — Gr8Boost ERP"
        description="How Gr8Boost ERP collects, uses and safeguards customer data. GDPR-aligned, with per-workspace data isolation and bcrypt-hashed credentials."
        canonical="https://gr8booster.com/privacy"
      />
      <Header />
      <main className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="text-[10px] uppercase tracking-[0.25em] text-[#FF4F00] font-mono">Legal · v1.0</div>
        <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tighter mt-2">Privacy notice</h1>
        <p className="text-[#495057] mt-3 text-sm">Effective February 2026 · Gr8booster (&ldquo;we&rdquo;, &ldquo;us&rdquo;)</p>

        <Section title="1. What we collect">
          <p>
            When you register a workspace we collect your name, work email, hashed password (bcrypt — we never store
            plaintext) and the data you choose to enter into your workspace (customers, invoices, inventory etc.).
            Each workspace is fully isolated — no other workspace can read your data.
          </p>
        </Section>
        <Section title="2. How we use it">
          <ul className="list-disc ml-5 space-y-1.5">
            <li>To operate the service (authentication, sending invoices, recording transactions).</li>
            <li>To process payments via our PCI-DSS-compliant partners (PayPal, Stripe, MTN MoMo).</li>
            <li>To send service emails — billing receipts, password resets, security alerts.</li>
            <li>To produce anonymous, aggregated product analytics so we can improve Gr8Boost ERP.</li>
          </ul>
          <p className="mt-3"><strong>We never sell your data, and we don&apos;t share it with advertisers.</strong></p>
        </Section>
        <Section title="3. Payment data">
          <p>
            Card details are entered directly into PayPal&apos;s hosted card fields — they <em>never</em> touch our servers.
            We only receive a payment confirmation reference. This keeps our PCI scope minimised.
          </p>
        </Section>
        <Section title="4. AI co-pilot data (Gr8Agentic AI)">
          <p>
            When you ask Gr8Agentic AI a question, your prompt and a scoped slice of your workspace data are sent to
            Anthropic (Claude Sonnet 4.5) to generate an answer. Anthropic does not train on enterprise API data.
            Prompts are not retained beyond the request.
          </p>
        </Section>
        <Section title="5. Your rights (GDPR / CCPA)">
          <ul className="list-disc ml-5 space-y-1.5">
            <li>Access — download all your workspace data on request.</li>
            <li>Correction — edit anything yourself in-app, or email us.</li>
            <li>Deletion — request workspace deletion and we&apos;ll erase within 30 days.</li>
            <li>Portability — export to CSV/JSON from any module.</li>
          </ul>
        </Section>
        <Section title="6. Security">
          <p>
            TLS 1.3 in transit, encryption at rest, bcrypt password hashes, per-tenant data isolation, rate-limited
            auth (10 logins / 5 min / IP), brute-force account lockout (5 fails / 5 min → 5 min lockout), and an
            immutable audit log for every mutation. The platform is OWASP-hardened and penetration-tested.
          </p>
        </Section>
        <Section title="7. Contact">
          <p>
            Questions? Email <a className="underline" href="mailto:privacy@gr8booster.com">privacy@gr8booster.com</a> or contact our sales team from the landing page.
          </p>
        </Section>
      </main>
      <Footer />
    </div>
  );
}

function Section({ title, children }) {
  return (
    <section className="mt-10">
      <h2 className="font-heading text-xl font-bold tracking-tight">{title}</h2>
      <div className="text-[#495057] text-[15px] leading-relaxed mt-3">{children}</div>
    </section>
  );
}

function Header() {
  return (
    <header className="border-b border-[#DEE2E6]">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2"><Logo size={28} /></Link>
        <Link to="/" className="text-sm text-[#495057] hover:text-[#FF4F00]">← Back to gr8booster.com</Link>
      </div>
    </header>
  );
}

function Footer() {
  return (
    <footer className="border-t border-[#DEE2E6] py-8">
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8 text-[10px] uppercase tracking-[0.2em] text-[#495057] flex flex-wrap gap-x-6 gap-y-2 justify-between">
        <div>© {new Date().getFullYear()} Gr8booster · Privacy notice</div>
        <div className="flex gap-4">
          <Link to="/terms" className="hover:text-[#0A0A0A]">Terms</Link>
          <Link to="/" className="hover:text-[#0A0A0A]">Home</Link>
        </div>
      </div>
    </footer>
  );
}
